import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useAuth } from '../../context/AuthContext';
import { submitFeedback } from '../../utils/feedbackApi';

const CATEGORIES = [
  { value: 'BUG', label: 'Bug Report' },
  { value: 'FEATURE_REQUEST', label: 'Feature Request' },
  { value: 'GENERAL', label: 'General Feedback' },
  { value: 'OTHER', label: 'Other' },
];

const MAX_MESSAGE_LENGTH = 1000;

const FeedbackDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const [category, setCategory] = useState('GENERAL');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      await submitFeedback({
        category,
        message: message.trim(),
        pageUrl: window.location.href,
        pageContext: window.location.pathname,
        userAgent: navigator.userAgent,
      });

      setSnackbar({
        open: true,
        message: 'Feedback submitted successfully. Thank you!',
        severity: 'success',
      });
      setCategory('GENERAL');
      setMessage('');
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to submit feedback. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const charactersRemaining = MAX_MESSAGE_LENGTH - message.length;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
          },
        }}
      >
        <DialogTitle>Send Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share your thoughts, report bugs, or request features. Your feedback helps us improve.
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="feedback-category-label">Category</InputLabel>
              <Select
                labelId="feedback-category-label"
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
                disabled={submitting}
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              placeholder="Describe your feedback in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              disabled={submitting}
              inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
              helperText={
                <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Be as specific as possible</span>
                  <span style={{ color: charactersRemaining < 100 ? '#f59e0b' : 'inherit' }}>
                    {charactersRemaining} characters remaining
                  </span>
                </Box>
              }
            />

            <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Submitted as:</strong> {user?.name || user?.email}
                <br />
                <strong>Current page:</strong> {window.location.pathname}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackDialog;
